import Card from '../../components/ui/Card';
import type { EvidenceRowDto } from './types';

// BP 27 — "an auditor asking 'how do you know this student attended meeting
// 7' must get a real answer, not a boolean." Every row here answers when it
// happened, what produced it (source table + id), and who recorded it.
const KIND_LABEL: Record<string, string> = {
  attendance: 'Attendance', participation: 'Participation', progress: 'Lesson progress',
  quiz_check: 'Quiz check', survey_completion: 'Survey completed', login: 'Login',
  note: 'Note', outcome: 'Outcome judgment', d2l_status: 'D2L status',
};

function recordedByLabel(rb: EvidenceRowDto['recorded_by']): string {
  if (!rb) return 'system';
  if (rb === 'system') return 'system';
  return rb.name || `user ${rb.id}`;
}

function fmt(d: string): string {
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function EvidenceTimeline({ evidence, limit }: { evidence: EvidenceRowDto[]; limit?: number }) {
  const rows = limit ? evidence.slice(-limit).reverse() : [...evidence].reverse();

  return (
    <Card className="overflow-hidden print:break-inside-avoid">
      <div className="border-b border-ws-border bg-ws-surface-2 px-4 py-2.5">
        <h3 className="text-sm font-semibold text-ws-ink">Evidence log</h3>
        <p className="text-[11px] text-ws-ink-3">Every row: when it happened, what produced it, who recorded it.</p>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-ws-ink-3">No evidence recorded yet.</p>
      ) : (
        <ul className="divide-y divide-ws-border">
          {rows.map((e, i) => (
            <li key={`${e.source.table}-${e.source.id}-${i}`} className={`flex items-start gap-3 px-4 py-2 text-[13px] ${e.superseded ? 'opacity-60' : ''}`}>
              <span className="mt-0.5 w-32 shrink-0 text-[11px] text-ws-ink-4">{fmt(e.at)}</span>
              <span className="w-28 shrink-0 text-[11px] font-medium text-ws-ink-3">{KIND_LABEL[e.kind] || e.kind}</span>
              <span className={`flex-1 text-ws-ink-2 ${e.superseded ? 'line-through' : ''}`}>
                {e.summary}
                {e.quality && <span className="ml-1.5 rounded bg-ws-surface-2 px-1.5 py-0.5 text-[10px] text-ws-ink-3">{e.quality}</span>}
                {(e.flags || []).map((f) => (
                  <span key={f} className="ml-1 rounded bg-ws-warning/15 px-1.5 py-0.5 text-[10px] text-ws-warning">{f}</span>
                ))}
              </span>
              <span className="w-28 shrink-0 text-right text-[11px] text-ws-ink-4">{recordedByLabel(e.recorded_by)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
